import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import * as R from 'ramda'

import Button from '../UniformComponents/Button'
import Modal from '../UniformComponents/Modal'
import FormController from '../components/form/FormController'
import RecordTag from '../components/RecordTags'

@observer
class UserEdit extends Component {
    constructor(props) {
        super(props)
        this.fields = {}
        this.state = {
            showDeleteConfirm: false,
        }
    }

    async componentDidUpdate(prevProps) {
        const { showModal } = this.props
        const { userId, userStore, handleCloseModal } = this.props

        if (showModal && !prevProps.showModal) {
            await userStore.initUser(userId, handleCloseModal)
        }
    }

    isValid = () => {
        const { userStore: { user } } = this.props
        return Object.keys(this.fields)
            .map((key) => (
                this.fields[key]({
                    value: user[key],
                    name: key,
                }, true)
            ))
            .every((valid) => valid)
    }

    areRecordTagsValid = () => {
        const { userStore } = this.props
        return userStore.availableTags.map((avTag) => {
            if (!avTag.isMandatory) return true
            const foundTag = userStore.recordTags[avTag.tagId]
            if (!foundTag) return false
            if (typeof foundTag === 'string' && !foundTag.length) return false
            return true
        }).every((valid) => valid)
    }

    register = (name, validate, path) => {
        if (path) {
            this.fields[name] = {
                validate,
                path,
            }
            return
        }
        this.fields[name] = validate
    }

    unregister = (name) => {
        Reflect.deleteProperty(this.fields, name)
    }

    save = async (sendUa = true) => {
        const {
            store, userId, userStore, handleCloseModal,
        } = this.props
        const { roles } = store.tokenData
        const { user, userAccess } = userStore

        const userRole = roles.split(',')
            .map((r) => parseInt(r, 10))
            .filter((r) => !Number.isNaN(r))[0]

        if (!this.isValid() || !this.areRecordTagsValid()) {
            return
        }

        const userData = Object.assign(
            { active: true },
            user.toJSON(userRole, store.isExploriAdmin()),
        )

        const ua = Array.from(userAccess.keys())
            .reduce((agg, key) => ([...agg, ...Array.from(userAccess.get(key).keys())]), [])
            .map((k) => k.split('_'))
            .filter(([oid]) => parseInt(oid, 10) === userStore.oid)
            .map(([oid, rid]) => ({
                userId: parseInt(userId, 10),
                accessRights: 6,
                oid: parseInt(oid, 10),
                rid: parseInt(rid, 10),
            }))

        const userRequest = {
            userData,
            userAccess: sendUa
                ? ua
                : undefined,
        }

        userStore.saveOrUpdate(userRequest, sendUa, handleCloseModal)
    }

    delete = async () => {
        const { userStore } = this.props
        const { user } = userStore

        user.active = false

        await this.save(false)

        this.setState({
            showDeleteConfirm: false,
        })
    }

    toggleConfirmDialog = () => {
        const { showDeleteConfirm } = this.state
        this.setState({
            showDeleteConfirm: !showDeleteConfirm,
        })
    }

    renderPwdResetLink = () => {
        const { store, userStore } = this.props
        const { passwordResetLink } = userStore
        let elem

        if (!R.isNil(passwordResetLink)) {
            elem = (
                <Button
                    label={i18n.t('Passwordreset_link')}
                    icon="fa-regular fa-copy"
                    cssClass="regular tertiary"
                    onClick={() => store.copyText(passwordResetLink, i18n.t('Passwordreset_link'))}
                />
            )
        }

        return elem
    }

    renderButtons = () => {
        const { userStore, store, handleCloseModal } = this.props
        const { tokenData } = store
        const { user } = userStore
        const showDelete = (
            user.userId !== 0
            && user.userId !== tokenData.userId
            && user.active
        )
        return (
            <div className="form-button-wrapper">
                {showDelete ? (
                    <Button
                        label={i18n.t('delete')}
                        icon="fa-regular fa-user-xmark"
                        cssClass="regular tertiary"
                        onClick={() => {
                            handleCloseModal()
                            this.toggleConfirmDialog()
                        }}
                    />
                ) : (
                    <Button
                        label={i18n.t('close')}
                        icon=""
                        cssClass="regular tertiary"
                        onClick={handleCloseModal}
                    />
                )}
                <Button
                    label={i18n.t('save')}
                    icon="fa-light fa-floppy-disk"
                    cssClass="regular primary"
                    onClick={() => this.save(true)}
                />
            </div>
        )
    }

    render() {
        const {
            showModal, handleCloseModal, userStore, userId,
        } = this.props
        const { showDeleteConfirm } = this.state

        return (
            <Fragment>
                <Modal show={showModal} onClose={handleCloseModal} loading={userStore.formLoading}>
                    <div className="form-header">
                        <h2>{userId !== 0 ? 'Edit user' : 'New user'}</h2>
                        <p className="desc">
                            {userId !== 0 ? 'Edit your user details below' : 'Enter your new user details below'}
                        </p>
                    </div>
                    <FormController
                        fields={this.fields}
                        formFields={userStore.formFields}
                        setField={userStore.setField}
                    />
                    <br />
                    <br />
                    <RecordTag
                        values={userStore.recordTags}
                        availableTags={userStore.availableTags}
                        onBlur={userStore.setTag}
                        register={this.register}
                        unregister={this.unregister}
                    />
                    {this.renderPwdResetLink()}
                    {this.renderButtons()}
                </Modal>

                <Modal
                    show={showDeleteConfirm}
                    onClose={this.toggleConfirmDialog}
                >
                    <div className="form-header">
                        <h2>{i18n.t('gen_confirm_popup_title')}</h2>
                        <p className="desc">{i18n.t('conf_user_delete')}</p>
                    </div>
                    <div className="form-button-wrapper">
                        <Button
                            icon="fa-regular fa-xmark"
                            cssClass="regular primary"
                            label={i18n.t('gen_no')}
                            onClick={this.toggleConfirmDialog}
                        />
                        <Button
                            icon="fa-regular fa-check"
                            cssClass="regular tertiary"
                            label={i18n.t('gen_yes')}
                            onClick={this.delete}
                        />
                    </div>
                </Modal>
            </Fragment>
        )
    }
}

export default UserEdit
